import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Stack, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, EventNote } from '@mui/icons-material';
import { useNavigate } from '@tanstack/react-router';
import {
  HouseholdRetirementService, type PlanEvent, type TimelineItem, type TimelineResponse,
} from '../services/household-retirement-service';
import { EventDialog, type AccountChoice } from '../components/timeline/EventDialog';
import { timelineDate, timelineIcon } from '../components/timeline/timelineIcons';

const money = (v: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v);

type Filter = 'all' | 'upcoming' | 'past' | 'mine';

/** Groups items by year, inserting the "Now" marker before the first upcoming item. */
export function groupTimeline(items: TimelineItem[]) {
  const groups: { year: number; items: (TimelineItem | 'now')[] }[] = [];
  let nowPlaced = false;
  for (const it of items) {
    let g = groups[groups.length - 1];
    if (!g || g.year !== it.year) {
      g = { year: it.year, items: [] };
      groups.push(g);
    }
    if (!nowPlaced && !it.past) {
      g.items.push('now');
      nowPlaced = true;
    }
    g.items.push(it);
  }
  return groups;
}

/**
 * The household's life on one line (issue #39): births, leave, the
 * mortgage, loans paid off, school and university, the investment plan,
 * retirements and pensions — derived from the plan and the data — plus the
 * household's own planned or remembered events, which can be added here.
 */
export const TimelinePage: React.FC = () => {
  const navigate = useNavigate();
  const [tl, setTl] = useState<TimelineResponse | null>(null);
  const [events, setEvents] = useState<PlanEvent[]>([]);
  const [accounts, setAccounts] = useState<AccountChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [dialog, setDialog] = useState<{ open: boolean; initial: PlanEvent | null }>({ open: false, initial: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, model] = await Promise.all([HouseholdRetirementService.timeline(), HouseholdRetirementService.get(true)]);
      setTl(t);
      setEvents(model.model?.events ?? []);
      const accts: AccountChoice[] = [];
      for (const a of model.inputs?.accounts ?? []) {
        if (a.kind === 'vehicle') accts.push({ id: a.account_id, name: a.name, kind: 'vehicle' });
      }
      for (const d of model.inputs?.debts ?? []) {
        if (d.account_id) accts.push({ id: d.account_id, name: d.label || d.name, kind: 'loan' });
      }
      setAccounts(accts);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load the timeline');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => (tl?.items ?? []).filter((it) =>
    filter === 'all' || (filter === 'upcoming' && !it.past) || (filter === 'past' && it.past) || (filter === 'mine' && it.source === 'user')),
  [tl, filter]);
  const groups = useMemo(() => groupTimeline(shown), [shown]);

  const saveEvent = async (e: PlanEvent) => {
    if (e.id) await HouseholdRetirementService.updateEvent(e.id, e);
    else await HouseholdRetirementService.addEvent(e);
    await load();
  };
  const removeEvent = async (id: string) => {
    if (!window.confirm('Delete this event?')) return;
    await HouseholdRetirementService.deleteEvent(id);
    await load();
  };

  return (
    <Box sx={{ p: 3 }} data-testid="timeline-page">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EventNote color="primary" />
          <Typography variant="h5">Timeline</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} aria-label="Timeline filter">
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="upcoming">Upcoming</ToggleButton>
            <ToggleButton value="past">Past</ToggleButton>
            <ToggleButton value="mine">My events</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, initial: null })}
            disabled={!tl?.configured}>Add event</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {tl && !tl.configured && (
        <Alert severity="info" sx={{ mb: 2 }}>Start a household plan (FIRE planner → Household plan) to see births, leave, school, retirements and your own events here.</Alert>
      )}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
        <Stack spacing={2}>
          {groups.map((g) => (
            <Card key={g.year} variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>{g.year}</Typography>
                <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label={`Events in ${g.year}`}>
                  {g.items.map((it, i) => it === 'now' ? (
                    <Box component="li" key={`now-${i}`} data-testid="now-marker" sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                      <Box sx={{ flex: 1, borderTop: 2, borderColor: 'primary.main' }} />
                      <Chip size="small" color="primary" variant="outlined" label={`Now · ${timelineDate(tl!.as_of, true)}`} />
                      <Box sx={{ flex: 1, borderTop: 2, borderColor: 'primary.main' }} />
                    </Box>
                  ) : (
                    <Box component="li" key={it.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, opacity: it.past ? 0.65 : 1,
                      borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" className="fw-num" sx={{ width: 96, flexShrink: 0, color: 'text.secondary' }}>
                        {timelineDate(it.date)}
                      </Typography>
                      <Box sx={{ color: it.source === 'user' ? 'secondary.main' : 'primary.main', display: 'flex' }}>{timelineIcon(it.icon)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ cursor: it.link ? 'pointer' : 'default' }}
                          onClick={() => it.link && navigate({ to: it.link.split('?')[0] as any, search: it.link.includes('tab=household') ? { tab: 'household' } as any : undefined })}>
                          {it.label}
                        </Typography>
                        {(it.window || it.amount_assumed) && (
                          <Typography variant="caption" color="text.secondary">
                            {it.window ? `window ${it.window}` : ''}{it.window && it.amount_assumed ? ' · ' : ''}{it.amount_assumed ? 'amount estimated' : ''}
                          </Typography>
                        )}
                      </Box>
                      {it.amount != null && (
                        <Typography variant="body2" className="fw-num" color={it.amount < 0 ? 'error.main' : 'success.main'}>
                          {it.amount > 0 ? '+' : ''}{money(it.amount)}
                        </Typography>
                      )}
                      <Chip size="small" variant="outlined" label={it.source === 'user' ? 'yours' : it.source} />
                      {it.event_id && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" aria-label={`Edit ${it.label}`}
                              onClick={() => setDialog({ open: true, initial: events.find((e) => e.id === it.event_id) ?? null })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" aria-label={`Delete ${it.label}`} onClick={() => void removeEvent(it.event_id!)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
          {tl && <Typography variant="caption" color="text.secondary">{tl.disclaimer}</Typography>}
        </Stack>
      )}
      <EventDialog open={dialog.open} initial={dialog.initial} accounts={accounts}
        onClose={() => setDialog({ open: false, initial: null })} onSave={saveEvent} />
    </Box>
  );
};
